import { PROC_POOL_GEOMETRY } from '../objects/Pool/config/constants';

const VALID_BORDER_TYPES = ['normal', 'wide', 'narrow'];

const {
    SCALE_XZ,
    BASE_CONCRETE_THICKNESS,
    BOTTOM_CONCRETE_THICKNESS,
    SKIMMER_BEVEL_SIZE,
    WALL_HEIGHT_FACTOR,
    POOL_GROUP_Y_OFFSET
} = PROC_POOL_GEOMETRY;

/** Как в OverflowSquarePoolModel — внешний бетонный борт до фаски. */
const OVERFLOW_RIM_CONCRETE_THICKNESS = 0.3;

/** Ширины лотка (мм) для переливного бассейна — тот же набор, что в UI сцены. */
export const TROUGH_WIDTH_MM_OPTIONS = [150, 180, 200, 250];
export const DEFAULT_TROUGH_WIDTH_MM = 200;

/** Тип борта по умолчанию (скиммерный) — совпадает с начальным состоянием сцены. */
export const DEFAULT_BORDER_TYPE = 'normal';

/** Варианты борта для скиммерного бассейна (порядок в меню снизу). */
export const BORDER_TYPE_OPTIONS = [
    { id: 'narrow', label: 'Узкий' },
    { id: 'normal', label: 'Обычный' },
    { id: 'wide',   label: 'Широкий' }
];

/** Сбрасывает к {@link DEFAULT_BORDER_TYPE}, если значение невалидно. */
export function normalizeBorderType(value) {
    return VALID_BORDER_TYPES.includes(value) ? value : DEFAULT_BORDER_TYPE;
}

export function normalizeTroughWidth(value, fallback = DEFAULT_TROUGH_WIDTH_MM) {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Таблица подбора ширины лотка по площади бассейна (владелец, уточнено 2026-07-26).
 * Площадь = length × width. Верхняя граница каждой строки включена в неё же
 * ("до 40 м²" / "40–100 м²" читаются как area <= 40 → 150мм, дальше → 180мм).
 *
 * Для справки (не используется в расчёте напрямую — ширина уже сопоставлена
 * по площади, а не по расходу; ориентировочная производительность системы
 * по ширине лотка: 150мм — до 20 м³/ч, 180мм — 20–45 м³/ч, 200мм — 45–80 м³/ч,
 * 250мм — 80–160 м³/ч).
 */
const TROUGH_WIDTH_AREA_TABLE = [
    { widthMm: 150, maxAreaM2: 40 },
    { widthMm: 180, maxAreaM2: 100 },
    { widthMm: 200, maxAreaM2: 300 },
    { widthMm: 250, maxAreaM2: 625 },
];

/**
 * Рекомендуемая ширина лотка (мм) по площади бассейна. Площадь за пределами
 * таблицы (>625 м²) получает максимальную ширину из каталога — больше 250мм
 * вариантов нет, а урезать рекомендацию до null для самых больших бассейнов
 * было бы менее полезно, чем показать лучший доступный вариант.
 * @param {{length?: number|string, width?: number|string}} poolDimensions
 * @returns {number|null}
 */
export function getRecommendedTroughWidthMm(poolDimensions) {
    const length = parseFloat(poolDimensions?.length);
    const width = parseFloat(poolDimensions?.width);
    if (!Number.isFinite(length) || !Number.isFinite(width) || length <= 0 || width <= 0) {
        return null;
    }

    const areaM2 = length * width;
    const match = TROUGH_WIDTH_AREA_TABLE.find((row) => areaM2 <= row.maxAreaM2);
    return match ? match.widthMm : TROUGH_WIDTH_AREA_TABLE[TROUGH_WIDTH_AREA_TABLE.length - 1].widthMm;
}

/**
 * Половины габарита чаши по внешней кромке (как PoolDimensionRulers / процедурные модели).
 * Ось Z — длина L, X — ширина W.
 */
export function getPoolRimHalfExtents(poolLength, poolWidth, borderType, isOverflowPool) {
    const L = Math.max(0, Number(poolLength) || 0);
    const W = Math.max(0, Number(poolWidth) || 0);
    const xOuterHalf = (W * SCALE_XZ) / 2;
    const zOuterHalf = (L * SCALE_XZ) / 2;
    const xInnerHalf = Math.max(0, xOuterHalf - BASE_CONCRETE_THICKNESS);
    const zInnerHalf = Math.max(0, zOuterHalf - BASE_CONCRETE_THICKNESS);
    const bt = normalizeBorderType(borderType);
    const thicknessLocal = bt === 'wide' ? 0.2 : bt === 'narrow' ? 0.07 : 0.14;
    const rimHalfX = isOverflowPool
        ? xInnerHalf + OVERFLOW_RIM_CONCRETE_THICKNESS + SKIMMER_BEVEL_SIZE
        : xInnerHalf + thicknessLocal + SKIMMER_BEVEL_SIZE;
    const rimHalfZ = isOverflowPool
        ? zInnerHalf + OVERFLOW_RIM_CONCRETE_THICKNESS + SKIMMER_BEVEL_SIZE
        : zInnerHalf + thicknessLocal + SKIMMER_BEVEL_SIZE;
    return { rimHalfX, rimHalfZ };
}

/**
 * Полуразмеры внутренней поверхности чаши под воду (как innerX / innerZ в Simple/Overflow PoolModel).
 * Ось сцены: X — ширина W, Z — длина L.
 */
export function getPoolWaterInnerHalfExtents(poolLength, poolWidth) {
    const L = Math.max(0.1, Number(poolLength) || 4);
    const W = Math.max(0.1, Number(poolWidth) || 4);
    const LVis = L * SCALE_XZ;
    const WVis = W * SCALE_XZ;
    const innerHalfX = Math.max(0.05, WVis / 2 - BASE_CONCRETE_THICKNESS);
    const innerHalfZ = Math.max(0.05, LVis / 2 - BASE_CONCRETE_THICKNESS);
    return { innerHalfX, innerHalfZ };
}

/**
 * Мировая Y центра горизонтальной плоскости воды (тот же родитель, что у PoolModel без его внутреннего group).
 * Уровень чуть ниже верхней кромки стены, как в PoolDimensionRulers / решётках.
 */
export function getPoolWaterSurfaceWorldY(poolDepth, poolType) {
    const D = Math.max(0.1, Number(poolDepth) || 1.5);
    const wallHeight = D * WALL_HEIGHT_FACTOR;
    const isOverflow = String(poolType || '').toLowerCase().includes('перелив');
    // Скиммерный: уровень ниже кромки (рабочий уровень воды); перелив — ближе к нулю лотка
    const belowRim = isOverflow ? -0.002 : 0.034;
    return POOL_GROUP_Y_OFFSET + wallHeight - belowRim;
}

/** Отступ стеновых форсунок от верхнего края чаши (м, физические). */
export const NOZZLE_BELOW_TOP_RIM_M = 0.35;

/**
 * Мировая Y крепления стеновой форсунки: верх внутренней стены минус 35 см (в метрах глубины).
 * Совпадает с геометрией ProceduralPoolGrid (POOL_GROUP_Y_OFFSET + дно + wallHeight).
 * @param {number} poolDepth
 * @returns {number}
 */
export function getNozzleWallMountWorldY(poolDepth) {
    const D = Math.max(0.1, Number(poolDepth) || 1.5);
    const wallHeight = D * WALL_HEIGHT_FACTOR;
    const wallTopY = POOL_GROUP_Y_OFFSET + BOTTOM_CONCRETE_THICKNESS + wallHeight;
    const belowTopScene = NOZZLE_BELOW_TOP_RIM_M * WALL_HEIGHT_FACTOR;
    return wallTopY - belowTopScene;
}

/** Отступ ПЫЛЕСОСНОЙ форсунки от верхнего края чаши (м, физические) — отдельно
 * от обычных стеновых форсунок (владелец, 2026-07-27: 30 см, не 35 см). */
export const VACUUM_NOZZLE_BELOW_TOP_RIM_M = 0.30;

/**
 * Мировая Y крепления пылесосной форсунки: верх внутренней стены минус 30 см.
 * Та же геометрия, что и getNozzleWallMountWorldY, но со своим отступом.
 * @param {number} poolDepth
 * @returns {number}
 */
export function getVacuumNozzleWallMountWorldY(poolDepth) {
    const D = Math.max(0.1, Number(poolDepth) || 1.5);
    const wallHeight = D * WALL_HEIGHT_FACTOR;
    const wallTopY = POOL_GROUP_Y_OFFSET + BOTTOM_CONCRETE_THICKNESS + wallHeight;
    const belowTopScene = VACUUM_NOZZLE_BELOW_TOP_RIM_M * WALL_HEIGHT_FACTOR;
    return wallTopY - belowTopScene;
}
